/**
 * Catálogo de módulos do SIGI — fonte única de verdade para:
 *   - menu lateral (app/layouts/nav.ts): cada seção do menu é um módulo;
 *   - home (menu de módulos em "/");
 *   - busca global (CommandPalette) e trilha de navegação (breadcrumbs).
 *
 * Regras:
 *   - `permission` é a permissão de ENTRADA no módulo (baseline `*:consultar`);
 *     o módulo só aparece para quem a possui.
 *   - `flag` liga/desliga via VITE_MODULE_*_ENABLED (src/lib/modules.ts).
 *   - Cada item pode exigir uma permissão própria (ação) além da de entrada.
 */
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Briefcase,
  Building2,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Factory,
  FileBarChart2,
  FileCheck,
  FileText,
  GraduationCap,
  HardHat,
  Landmark,
  LayoutGrid,
  ListChecks,
  ListTodo,
  Map,
  MapPin,
  MonitorSmartphone,
  PackageSearch,
  PlugZap,
  Plus,
  Scale,
  Settings2,
  ShieldAlert,
  ShieldUser,
  Tags,
  UserSearch,
  Users,
  Warehouse,
} from "lucide-react";

import { PERMISSOES, type Permissao } from "@/hooks/usePermissions";
import type { ModuleName } from "@/lib/modules";

export interface ModuloItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Ação exigida; se ausente, basta a permissão de entrada do módulo. */
  permission?: Permissao;
}

export interface Modulo {
  id: string;
  nome: string;
  descricao: string;
  icon: LucideIcon;
  /** Rota de entrada do módulo (usada pela página de módulos). */
  rota: string;
  /** Permissão de entrada (baseline `*:consultar`). */
  permission: Permissao;
  /** Feature flag em src/lib/modules.ts. */
  flag: ModuleName;
  itens: ModuloItem[];
}

export const MODULOS: Modulo[] = [
  {
    id: "inspecao",
    nome: "Inspeções",
    descricao:
      "Programação, trabalho de campo, inconformidades e emissão da ficha",
    icon: ClipboardCheck,
    rota: "/inspecoes",
    permission: PERMISSOES.inspecao.consultar,
    flag: "inspecao",
    itens: [
      { label: "Painel", to: "/inspecoes", icon: LayoutGrid },
      { label: "Inspeções", to: "/inspecoes/lista", icon: ListTodo },
      { label: "Nova inspeção", to: "/inspecoes/nova", icon: Plus },
      { label: "Relatórios", to: "/inspecoes/relatorios", icon: FileBarChart2 },
      {
        label: "Tipos de Inspeção",
        to: "/inspecoes/tipos",
        icon: Tags,
        permission: PERMISSOES.inspecao.editar,
      },
      {
        label: "Itens de Inspeção",
        to: "/inspecoes/atividades",
        icon: ClipboardList,
        permission: PERMISSOES.inspecao.editar,
      },
      {
        label: "Normas",
        to: "/inspecoes/normas",
        icon: Scale,
        permission: PERMISSOES.inspecao.editar,
      },
      {
        label: "Como inspecionar",
        to: "/inspecoes/tutorial",
        icon: GraduationCap,
      },
    ],
  },
  {
    id: "incidente",
    nome: "Incidentes",
    descricao:
      "Eventos adversos reais (acidentes, quase-acidentes), investigação e CAT",
    icon: ShieldAlert,
    rota: "/incidentes",
    permission: PERMISSOES.incidente.consultar,
    flag: "incidente",
    itens: [
      { label: "Painel", to: "/incidentes/overview", icon: LayoutGrid },
      { label: "Incidentes", to: "/incidentes", icon: ShieldAlert },
      {
        label: "Relatórios",
        to: "/incidentes/relatorios",
        icon: FileBarChart2,
      },
      {
        label: "Novo incidente",
        to: "/incidentes/novo",
        icon: Plus,
        permission: PERMISSOES.incidente.criar,
      },
      {
        label: "Tipos de Incidente",
        to: "/incidentes/tipos",
        icon: Tags,
        permission: PERMISSOES.incidente.editar,
      },
      {
        label: "Tipos de documento",
        to: "/incidentes/tipos-documento",
        icon: FileText,
        permission: PERMISSOES.incidente.editar,
      },
    ],
  },
  {
    id: "agendamento",
    nome: "Agendamento",
    descricao: "Compromissos, agendamentos e calendário de equipes",
    icon: CalendarDays,
    rota: "/agendamento",
    permission: PERMISSOES.agendamento.consultar,
    flag: "agendamento",
    itens: [
      { label: "Painel", to: "/agendamento", icon: LayoutGrid },
      {
        label: "Calendário",
        to: "/agendamento/calendario",
        icon: CalendarClock,
      },
      { label: "Agendamentos", to: "/agendamento/lista", icon: ListTodo },
      {
        label: "Tipos de Evento",
        to: "/agendamento/tipos-evento",
        icon: CalendarClock,
      },
      {
        label: "Como usar",
        to: "/agendamento/tipos-evento/guia",
        icon: GraduationCap,
      },
    ],
  },
  {
    id: "checklist",
    nome: "Checklists",
    descricao: "Templates, versões e itens de verificação",
    icon: ListChecks,
    rota: "/checklists",
    permission: PERMISSOES.checklist.consultar,
    flag: "checklist",
    itens: [
      { label: "Painel", to: "/checklists", icon: LayoutGrid },
      { label: "Templates", to: "/checklists/templates", icon: ListChecks },
    ],
  },
  {
    id: "epi",
    nome: "EPIs",
    descricao: "Estoque, solicitações, entregas e fichas de EPI",
    icon: HardHat,
    rota: "/epis",
    permission: PERMISSOES.epi.consultar,
    flag: "epi",
    itens: [{ label: "Painel de EPIs", to: "/epis", icon: HardHat }],
  },
  {
    id: "forms",
    nome: "Formulários",
    descricao: "Requerimentos dinâmicos, versões e respostas",
    icon: FileText,
    rota: "/formularios",
    permission: PERMISSOES.forms.consultar,
    flag: "forms",
    itens: [
      { label: "Painel", to: "/formularios", icon: LayoutGrid },
      { label: "Formulários", to: "/formularios/lista", icon: FileText },
      { label: "Máscaras", to: "/formularios/mascaras", icon: Tags },
      {
        label: "Novo formulário",
        to: "/formularios/novo",
        icon: Plus,
        permission: PERMISSOES.forms.criar,
      },
    ],
  },
  {
    id: "almoxarifado",
    nome: "Almoxarifado",
    descricao: "Categorias, estoque, movimentações e solicitações",
    icon: Warehouse,
    rota: "/almoxarifado",
    permission: PERMISSOES.almoxarifado.consultar,
    flag: "almoxarifado",
    itens: [
      { label: "Painel", to: "/almoxarifado", icon: LayoutGrid },
      {
        label: "Categorias",
        to: "/almoxarifado/categorias",
        icon: Tags,
        permission: PERMISSOES.almoxarifado.categoriaGerenciar,
      },
      {
        label: "Equipamentos",
        to: "/almoxarifado/equipamentos",
        icon: PackageSearch,
      },
      {
        label: "Estoque",
        to: "/almoxarifado/estoque",
        icon: Warehouse,
        permission: PERMISSOES.almoxarifado.estoqueGerenciar,
      },
      {
        label: "Solicitações",
        to: "/almoxarifado/solicitacoes",
        icon: ClipboardCheck,
        permission: PERMISSOES.almoxarifado.solicitar,
      },
      {
        label: "Relatórios",
        to: "/almoxarifado/relatorios",
        icon: FileBarChart2,
        permission: PERMISSOES.almoxarifado.relatorio,
      },
    ],
  },
  {
    id: "pbdoc",
    nome: "PbDoc",
    descricao: "Integração e assinatura de documentos oficiais",
    icon: FileCheck,
    rota: "/pbdoc",
    permission: PERMISSOES.pbdoc.consultar,
    flag: "pbdoc",
    itens: [{ label: "PbDoc", to: "/pbdoc", icon: FileCheck }],
  },
  {
    id: "seguir",
    nome: "Painel de Obras",
    descricao:
      "Métricas de obras, malha rodoviária e obras relevantes (Seguir)",
    icon: Map,
    rota: "/seguir",
    permission: PERMISSOES.seguir.consultar,
    flag: "seguir",
    itens: [{ label: "Painel de Obras", to: "/seguir", icon: Map }],
  },
  {
    id: "cadastro",
    nome: "Cadastro",
    descricao: "Localidades (terminais e residências rodoviárias) e seus tipos",
    icon: MapPin,
    rota: "/cadastro/localidades",
    permission: PERMISSOES.cadastro.localidadeConsultar,
    flag: "cadastro",
    itens: [
      { label: "Localidades", to: "/cadastro/localidades", icon: MapPin },
      {
        label: "Cidades",
        to: "/cadastro/cidades",
        icon: Building2,
        permission: PERMISSOES.cadastro.cidadeConsultar,
      },
      {
        label: "Tipos de Localidade",
        to: "/cadastro/tipos-localidade",
        icon: Tags,
        permission: PERMISSOES.cadastro.localidadeGerenciar,
      },
      {
        label: "Empresas",
        to: "/cadastro/empresas",
        icon: Factory,
        permission: PERMISSOES.cadastro.empresaConsultar,
      },
      {
        label: "Dados institucionais",
        to: "/cadastro/institucional",
        icon: Landmark,
        permission: PERMISSOES.cadastro.institucionalConsultar,
      },
      {
        label: "Expedientes",
        to: "/cadastro/expedientes",
        icon: Clock,
        permission: PERMISSOES.cadastro.expedienteConsultar,
      },
      {
        label: "Documentos legais",
        to: "/cadastro/documentos-legais",
        icon: FileText,
        permission: PERMISSOES.cadastro.documentoConsultar,
      },
    ],
  },
  {
    id: "rh",
    nome: "RH",
    descricao: "Funcionários, setores, cargos e regimes de trabalho",
    icon: Users,
    rota: "/rh/funcionarios",
    permission: PERMISSOES.rh.consultar,
    flag: "rh",
    itens: [
      { label: "Funcionários", to: "/rh/funcionarios", icon: Users },
      { label: "Setores", to: "/rh/setores", icon: Building2 },
      { label: "Cargos", to: "/rh/cargos", icon: Briefcase },
      {
        label: "Regimes de trabalho",
        to: "/rh/regimes-trabalho",
        icon: CalendarClock,
      },
    ],
  },
  {
    id: "admin",
    nome: "Administração",
    descricao: "Grupos de trabalho, membros e visão de usuários",
    icon: ShieldUser,
    rota: "/admin/grupos",
    permission: PERMISSOES.admin.gerenciar,
    flag: "admin",
    itens: [
      { label: "Grupos", to: "/admin/grupos", icon: Users },
      { label: "Usuários", to: "/admin/usuarios", icon: UserSearch },
      {
        label: "Usuários logados",
        to: "/admin/sessoes",
        icon: MonitorSmartphone,
      },
      { label: "Configurações", to: "/admin/configuracoes", icon: Settings2 },
      { label: "Integração PbDoc", to: "/admin/pbdoc", icon: PlugZap },
      { label: "Diagnóstico", to: "/admin/diagnostico", icon: Activity },
    ],
  },
];

/** Módulos visíveis para o conjunto de permissões informado. */
export function modulosVisiveis(
  tem: (permissao: Permissao) => boolean,
  habilitado: (flag: ModuleName) => boolean,
): Modulo[] {
  return MODULOS.filter((m) => habilitado(m.flag) && tem(m.permission)).map(
    (m) => ({
      ...m,
      itens: m.itens.filter((i) => !i.permission || tem(i.permission)),
    }),
  );
}

/** Rótulos curtos por segmento de URL, para a trilha de navegação. */
export const ROTULOS_ROTA: Record<string, string> = {
  modulos: "Módulos",
  perfil: "Meu perfil",
  admin: "Administração",
  grupos: "Grupos",
  usuarios: "Usuários",
  sessoes: "Usuários logados",
  configuracoes: "Configurações",
  diagnostico: "Diagnóstico",
  inspecoes: "Inspeções",
  agendamento: "Agendamento",
  checklists: "Checklists",
  templates: "Templates",
  epis: "EPIs",
  almoxarifado: "Almoxarifado",
  pbdoc: "PbDoc",
  seguir: "Painel de Obras",
  cadastro: "Cadastro",
  localidades: "Localidades",
  cidades: "Cidades",
  empresas: "Empresas",
  institucional: "Dados institucionais",
  expedientes: "Expedientes",
  "documentos-legais": "Documentos legais",
  privacidade: "Política de Privacidade",
  termos: "Termos de Uso",
  "tipos-localidade": "Tipos de Localidade",
  relatorios: "Relatórios",
  lista: "Lista",
  nova: "Nova",
  calendario: "Calendário",
  "tipos-evento": "Tipos de Evento",
  guia: "Como usar os tipos de evento",
  atividades: "Itens de Inspeção",
  normas: "Normas",
  tipos: "Tipos de Inspeção",
  tutorial: "Como inspecionar",
  incidentes: "Incidentes",
  novo: "Novo",
  rh: "RH",
  funcionarios: "Funcionários",
  setores: "Setores",
  cargos: "Cargos",
  categorias: "Categorias",
  estoque: "Estoque",
  solicitacoes: "Solicitações",
  editar: "Editar",
};
